'use client';

/**
 * BulkActionsToolbar Component
 *
 * Appears when rows are selected, showing selection count and bulk actions.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { BulkAction } from './types';
import { defaultTheme, getTextStyles } from './theme/tokens';

interface BulkActionsToolbarProps<TData = any> {
  selectedCount: number;
  selectedRows: TData[];
  selectedIndices: number[];
  actions: BulkAction<TData>[];
  onClearSelection: () => void;
  onAction?: (actionId: string, rows: TData[]) => void;
  i18n?: {
    selectedCount?: (count: number) => string;
  };
}

export function BulkActionsToolbar<TData = any>({
  selectedCount,
  selectedRows,
  selectedIndices,
  actions,
  onClearSelection,
  onAction,
  i18n,
}: BulkActionsToolbarProps<TData>) {
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<BulkAction<TData> | null>(null);

  // Get typography styles (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  if (selectedCount === 0) return null;

  const handleAction = async (action: BulkAction<TData>) => {
    // Check if confirmation required
    if (action.confirm) {
      setConfirmAction(action);
      return;
    }

    await executeAction(action);
  };

  const executeAction = async (action: BulkAction<TData>) => {
    setProcessingAction(action.id);
    try {
      await action.onClick(selectedRows, selectedIndices);
      onAction?.(action.id, selectedRows);
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setProcessingAction(null);
      setConfirmAction(null);
    }
  };

  const isActionDisabled = (action: BulkAction<TData>) => {
    if (processingAction === action.id) return true;
    if (action.disabled && typeof action.disabled === 'function') {
      return action.disabled(selectedRows);
    }
    return false;
  };

  const getActionClassName = (action: BulkAction<TData>) => {
    switch (action.kind) {
      case 'destructive':
        return 'text-red-700 hover:bg-red-50';
      case 'primary':
        return 'bg-primary-600 text-white hover:bg-primary-700';
      default:
        return '';
    }
  };

  const getConfirmMessage = () => {
    if (!confirmAction) return '';
    if (typeof confirmAction.confirm === 'object' && confirmAction.confirm.message) {
      return confirmAction.confirm.message(selectedCount);
    }
    return `Are you sure you want to perform this action on ${selectedCount} item${
      selectedCount !== 1 ? 's' : ''
    }?`;
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 px-6 py-3 bg-[var(--dt-bg-bulk-toolbar)]">
        {/* Left: Selection count */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="h-8 w-8 p-0"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
          <span style={textStyles.primary()}>
            {i18n?.selectedCount
              ? i18n.selectedCount(selectedCount)
              : `${selectedCount} selected`}
          </span>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {actions.map((action) => {
            const Icon = action.icon;
            const disabled = isActionDisabled(action);
            const isProcessing = processingAction === action.id;

            return (
              <Button
                key={action.id}
                variant={action.kind === 'primary' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleAction(action)}
                disabled={disabled}
                className={cn(
                  getActionClassName(action),
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {Icon && (
                  <Icon className={cn('h-4 w-4 mr-2', isProcessing && 'animate-spin')} />
                )}
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(open) => !open && setConfirmAction(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {typeof confirmAction.confirm === 'object' && confirmAction.confirm.title
                  ? confirmAction.confirm.title
                  : 'Confirm Bulk Action'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {getConfirmMessage()}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.cancelLabel || 'Cancel'
                  : 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => executeAction(confirmAction)}
                className={cn(
                  confirmAction.kind === 'destructive' &&
                    'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                )}
              >
                {typeof confirmAction.confirm === 'object'
                  ? confirmAction.confirm.confirmLabel || 'Confirm'
                  : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
